import type { Row } from '@tanstack/react-table';

import type { Importer, IRejectionBreakdownLine } from '@feathr/blackbox';

/**
 * Whether a row expands to the run-details panel. Every draft, pending and failed import does, and
 * the panel says what state it is in. A complete import does once the worker stamped
 * `date_started` (Feathr/anhinga#8511): one the old importer ran has no stamp, and keeps today's
 * row (Feathr/anhinga#8646 gates the screens on the record, not only on the account's flags).
 */
export function hasRunDetails(importer: Importer): boolean {
  if (importer.get('state') === 'complete') {
    return Boolean(importer.get('date_started'));
  }
  return true;
}

/**
 * Whether a worker has claimed the run and stamped `date_started`. A draft's stamp, and a pending
 * import's when it is not running, is the last run's.
 */
export function hasRunStarted(importer: Importer): boolean {
  const state = importer.get('state');
  if (state === 'draft' || (state === 'pending' && !importer.get('is_running'))) {
    return false;
  }
  return Boolean(importer.get('date_started'));
}

/** Whether the import is complete or failed. */
export function hasRunFinished(importer: Importer): boolean {
  const state = importer.get('state');
  return state === 'complete' || state === 'failed';
}

/**
 * How many rows a fully imported run wrote: a complete import that skipped none. The count is the
 * people it created plus the people it updated. `undefined` for any other import, including a
 * complete one that wrote nothing.
 */
export function getFullyImportedRowCount(importer: Importer): number | undefined {
  if (importer.get('state') !== 'complete' || importer.get('skipped', 0) > 0) {
    return undefined;
  }
  const count = getImportedRowCount(importer);
  return count > 0 ? count : undefined;
}

/** `TanStackTable`'s `getRowCanExpand`, at module scope so its identity is stable. */
export function canExpandImportRow(row: Row<Importer>): boolean {
  return hasRunDetails(row.original);
}

/** How many rows the run wrote: the people it created plus the people it updated. */
export function getImportedRowCount(importer: Importer): number {
  return importer.get('inserted', 0) + importer.get('duplicates', 0);
}

/** Whether a complete import imported some rows and skipped others. */
function isCompleteWithSkippedRows(importer: Importer): boolean {
  return (
    importer.get('state') === 'complete' &&
    getImportedRowCount(importer) > 0 &&
    importer.get('skipped', 0) > 0
  );
}

/**
 * The row counts of a run that failed partway, or that completed with some rows imported and some
 * skipped. `skipped` includes the rows a failed run never reached, so imported plus skipped is
 * every row it read.
 */
export function getPartialImportCounts(
  importer: Importer,
): { imported: number; skipped: number; total: number } | undefined {
  if (importer.get('state') !== 'failed' && !isCompleteWithSkippedRows(importer)) {
    return undefined;
  }
  const imported = getImportedRowCount(importer);
  const skipped = importer.get('skipped', 0);
  const total = imported + skipped;
  return total > 0 ? { imported, skipped, total } : undefined;
}

/** The rejection breakdown lines worth showing, most rows first. */
export function getRejectionLines(importer: Importer): IRejectionBreakdownLine[] {
  return (importer.get('rejection_breakdown') ?? [])
    .filter((line) => line.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** Whether a complete import wrote no rows. */
export function hasNothingImported(importer: Importer): boolean {
  return importer.get('state') === 'complete' && getImportedRowCount(importer) === 0;
}

/**
 * Whether a complete contact import created no group. A missing `segment` alone is not enough: it
 * is also missing once the group is deleted, and before the server attaches a new import's group.
 */
export function hasNoGroup(importer: Importer): boolean {
  return (
    importer.get('state') === 'complete' &&
    importer.get('kind') !== 'partner' &&
    !importer.get('segment') &&
    getImportedRowCount(importer) === 0
  );
}
